import { attribution } from 'lib/sitePublicApi';
const ROOT = '/api/site/v1/commerce';
export const commerceKey = () => crypto.randomUUID();
const messages = {
  commerce_disabled: 'Checkout is not available yet. Your cart is saved; please try again later.',
  site_backend_disabled: 'Checkout is not available yet. Your cart is saved; please try again later.',
  provider_unavailable: 'This payment method is not available yet. Choose another method or try again later.',
  payment_provider_unavailable: 'This payment method is not available yet. Choose another method or try again later.',
  invalid_input: 'Check your contact details and cart, then try again.',
  invalid_customer: 'Enter your name, email, phone and at least one DOT or MC number.',
  duplicate_cart_item: 'Review your cart before trying again.',
  idempotency_conflict: 'This checkout request changed. Return to your cart to review it before trying again.',
  not_found: 'This order is not available in this browser.',
  order_not_found: 'This order is not available in this browser.',
  cart_session_required: 'Open checkout in the browser you used to place your order.',
  cart_session_requires_sign_in: 'Your website session has expired. Sign in again to continue.',
  checkout_owned_by_another_session: 'This checkout belongs to another session. Sign in to the account used for this order.',
  checkout_expired: 'This payment session has expired. Contact us with your order reference.',
  checkout_review_required: 'This order needs a review before payment. Contact us with your order reference.',
  payment_configuration_changed: 'Payment is temporarily unavailable for this order. Contact us with your order reference.',
  stripe_unavailable: 'Stripe could not connect. Your payment has not been confirmed. Retry the same request.',
  forbidden: 'This order is not available in this browser.',
  rate_limited: 'Please wait a moment before trying again.',
  price_changed: 'The price changed since you opened checkout. Check the new total, then confirm again.',
  prices_unavailable: 'Prices are being updated. Your cart is saved; please try again in a moment.',
  customer_required: 'Sign in with your email first. Your cart stays here.',
  checkout_email_mismatch: 'Use the email you signed in with.',
  invalid_dot_number: 'Enter your DOT number (digits only).',
  invalid_mc_number: 'Enter your MC number, for example MC-123456.',
  invalid_phone: 'Enter a US phone number with area code.',
  address_required: 'Enter your company street address, city, state and ZIP code.',
  disclaimer_required: 'Confirm that you are a registered motor carrier.',
  legal_text_not_published: 'Checkout is not open yet. Please try again later.',
  customization_unavailable: 'Customization is not offered right now. Untick it in your cart to continue.',
  deposit_one_package: 'A Standard or Premium review deposit is paid on its own, for one package. Remove the other items from your cart.',
  offer_not_found: 'This payment link is not available for your account.',
  offer_unavailable: 'This payment link has already been used.',
  deposit_credit_expired: 'This payment link has expired: the deposit counts toward the package for 30 days. Please contact us.',
};
async function request(path, { method = 'GET', body, key, signal } = {}) {
  let response;
  try {
    response = await fetch(ROOT + path, {
      method, credentials: 'same-origin', cache: 'no-store', redirect: 'error', referrerPolicy: 'no-referrer', signal,
      headers: body === undefined ? {} : { 'Content-Type': 'application/json', 'Idempotency-Key': key || commerceKey() },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new Error('The connection was interrupted. Your payment has not been confirmed. Retry the same request.');
  }
  const result = await response.json().catch(() => null);
  if (!response.ok || !result || !Object.hasOwn(result, 'data')) {
    const error = new Error(messages[result?.error?.code] || 'Checkout could not connect. Your payment has not been confirmed. Please try again.');
    error.code = result?.error?.code || 'connection_unavailable';
    error.status = response.status;
    throw error;
  }
  return result.data;
}
const orderPath = id => {
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(id || '')) throw new Error('Invalid order reference.');
  return '/orders/' + encodeURIComponent(id);
};
export const getCommerceCatalog = signal => request('/catalog', { signal });
export const getPaperworkPrices = signal => request('/prices', { signal });
// A published legal text (L3/L4: first disclaimer, order legal step, deposit terms), or null when not published.
export async function getLegalText(kind) {
  try {
    const r = await fetch('/api/site/v1/legal/' + encodeURIComponent(kind), { credentials: 'omit', cache: 'no-store' });
    return r.ok ? (await r.json()).data || null : null;
  } catch { return null; }
}
// Attribution (and a stored referral, D18) is added here for every checkout caller. The server
// keeps it out of the idempotency digest, so a retry with the same key never conflicts.
export const createCheckout = (body, key, signal) => request('/checkout', { method: 'POST', body: { ...body, attribution: attribution() }, key, signal });
export const getCommerceOrder = (id, signal) => request(orderPath(id), { signal });
export const listCommerceOrders = (signal, after) => request('/orders' + (after ? '?after=' + encodeURIComponent(after) : ''), { signal });
export const resumeCommerceOrder = (id, key, signal) => request(orderPath(id) + '/resume', { method: 'POST', body: {}, key, signal });
